import React, { useState, useEffect, useRef, useCallback } from "react";
import TopBarDisplayTitleNoLocationSelect from "../home/top_bar_components/TopBarDisplayTitleNoLocationSelect";
import TabsTwo from "../requests/shared/helpers/TabsTwo";
import FourWeekOverview from "./FourWeekOverview";
import FinancialForecastingOverview from "./FinancialForecastingOverview";
import { API_get_overview } from "../../../constants";
import { getRightworkAPI } from "../requests/utilities/requests";
import LoadSpinner from "../../../utilities/LoadSpinner";

function OverviewLoadingState() {
  return (
    <div className="flex min-h-[calc(100vh-160px)] w-full items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="flex h-8 w-8 items-center justify-center">
          <LoadSpinner />
        </div>
        <p className="text-sm font-medium text-slate-500">
          Loading overview data...
        </p>
      </div>
    </div>
  );
}

function OverviewContent(props) {
  const cachedOverviewState =
    props.overviewCacheState?.companyId === props.homeCompany
      ? props.overviewCacheState
      : null;
  const cachedWeekCache = cachedOverviewState?.weekCache || {};
  const hasCachedWeeks = Object.keys(cachedWeekCache).length > 0;
  const cachedInitialLoadComplete =
    hasCachedWeeks;

  const [activeTab, setActiveTab] = useState("4-Week");
  const [overviewData, setOverviewData] = useState([]);
  const [isLoading, setIsLoading] = useState(
    Boolean(props.homeCompany) && !cachedInitialLoadComplete
  );
  const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(
    cachedInitialLoadComplete
  );
  const [weekOffset, setWeekOffset] = useState(
    cachedOverviewState?.weekOffset ?? -1
  );
  const [weekCache, setWeekCache] = useState(
    cachedWeekCache
  );
  const [overviewFetchRetryKey, setOverviewFetchRetryKey] = useState(0);
  const cacheCompanyRef = useRef(props.homeCompany);
  const [overviewError, setOverviewError] = useState(null);
  const [emptyReason, setEmptyReason] = useState(null);
  const hasLocalOverviewCache =
    hasCompletedInitialLoad ||
    overviewData.length > 0 ||
    Object.keys(weekCache).length > 0;
  const isMountedOverviewCacheInvalidation =
    Boolean(props.homeCompany) &&
    cacheCompanyRef.current === props.homeCompany &&
    props.overviewCacheState &&
    props.overviewCacheState.companyId !== props.homeCompany &&
    hasLocalOverviewCache;

  // Rebuild display when cache or offset changes
  const buildDisplayData = useCallback(() => {
    if (Object.keys(weekCache).length === 0) {
      setOverviewData([]);
      return;
    }

    const neededOffsets = [weekOffset, weekOffset + 1, weekOffset + 2, weekOffset + 3];
    const stores = Object.values(weekCache).map(storeCache => {
      const weekly_stats = neededOffsets
        .map(offset => storeCache.weeksByOffset?.[offset] || null);

      return {
        store_id: storeCache.store_id,
        store_name: storeCache.store_name,
        labor_percent_enabled: storeCache.labor_percent_enabled,
        recent_average_wage: storeCache.recent_average_wage,
        weekly_stats
      };
    }).sort((a, b) => a.store_name.localeCompare(b.store_name));

    setOverviewData(stores);
  }, [weekCache, weekOffset]);

  useEffect(() => {
    buildDisplayData();
  }, [buildDisplayData]);

  // Keep overview data warm while users drill into schedules and return.
  useEffect(() => {
    if (!props.homeCompany || !props.setOverviewCacheState) return;
    if (isMountedOverviewCacheInvalidation) return;
    props.setOverviewCacheState({
      companyId: props.homeCompany,
      weekCache,
      weekOffset,
      hasCompletedInitialLoad,
    });
  }, [
    props.homeCompany,
    props.setOverviewCacheState,
    weekCache,
    weekOffset,
    hasCompletedInitialLoad,
    isMountedOverviewCacheInvalidation,
  ]);

  // Reset cache only when the company changes. The overview endpoint returns
  // all stores for the company, so switching the active home store should not
  // force a full overview reload.
  useEffect(() => {
    if (
      cacheCompanyRef.current === props.homeCompany &&
      !isMountedOverviewCacheInvalidation
    ) {
      return;
    }

    cacheCompanyRef.current = props.homeCompany;
    setOverviewError(null);
    setEmptyReason(null);

    if (!props.homeCompany) {
      setOverviewData([]);
      setWeekCache({});
      setHasCompletedInitialLoad(false);
      setIsLoading(false);
      return;
    }

    if (isMountedOverviewCacheInvalidation) {
      setOverviewData([]);
      setWeekCache({});
      setHasCompletedInitialLoad(false);
      setIsLoading(true);
      return;
    }

    const cachedState =
      props.overviewCacheState?.companyId === props.homeCompany
        ? props.overviewCacheState
        : null;
    if (cachedState) {
      const cachedWeeks = cachedState.weekCache || {};
      const cachedLoadComplete =
        Object.keys(cachedWeeks).length > 0;

      setWeekCache(cachedWeeks);
      setWeekOffset(cachedState.weekOffset ?? -1);
      setHasCompletedInitialLoad(cachedLoadComplete);
      setIsLoading(!cachedLoadComplete);
    } else {
      setOverviewData([]);
      setWeekCache({});
      setWeekOffset(-1);
      setHasCompletedInitialLoad(false);
      setIsLoading(true);
    }
  }, [
    props.homeCompany,
    props.overviewCacheState,
    isMountedOverviewCacheInvalidation,
  ]);

  // Fetch missing weeks
  useEffect(() => {
    if (!props.homeCompany || !props.homeStore) {
      setIsLoading(false);
      setHasCompletedInitialLoad(false);
      return;
    }

    if (overviewError) return;
    let cancelled = false;

    const fetchMissingWeeks = async () => {
      if (hasCompletedInitialLoad && Object.keys(weekCache).length === 0) {
        setIsLoading(false);
        return;
      }

      const neededOffsets = [weekOffset, weekOffset + 1, weekOffset + 2, weekOffset + 3];
      const missingOffsets = neededOffsets.filter(offset => {
        const cachedStores = Object.values(weekCache);
        if (cachedStores.length === 0) return true;
        return !cachedStores.every(
          storeCache => storeCache.weeksByOffset?.[offset]
        );
      });

      if (missingOffsets.length === 0) {
        if (Object.keys(weekCache).length > 0) {
          setHasCompletedInitialLoad(true);
        }
        setIsLoading(false);
        return;
      }

      // On initial load (empty cache), fetch all 4 weeks at once
      // Otherwise, only fetch the first missing week (incremental navigation)
      const isInitialLoad = Object.keys(weekCache).length === 0;
      const fetchOffset = isInitialLoad ? weekOffset : missingOffsets[0];
      const fetchCount = isInitialLoad ? 4 : 1;

      setIsLoading(true);

      const api_params = {
        store_id: props.homeStore,
        company_id: props.homeCompany,
        week_count: fetchCount,
        week_offset: fetchOffset,
      };

      let didFetchSuccessfully = false;

      try {
        const res = await getRightworkAPI(API_get_overview, api_params);
        if (cancelled) return;
        const payload = res.data?.redux?.payload;
        if (!Array.isArray(payload?.stores)) {
          throw new Error("Invalid overview response");
        }
        const stores = payload.stores;
        setEmptyReason(payload.empty_reason || null);
        didFetchSuccessfully = true;
        setWeekCache(prevCache => {
          if (stores.length === 0) return {};
          const newCache = { ...prevCache };
          stores.forEach(store => {
            if (!newCache[store.store_id]) {
              newCache[store.store_id] = {
                store_id: store.store_id,
                store_name: store.store_name,
                labor_percent_enabled: store.labor_percent_enabled,
                recent_average_wage: store.recent_average_wage,
                weeksByOffset: {}
              };
            }
            newCache[store.store_id].labor_percent_enabled =
              store.labor_percent_enabled;
            newCache[store.store_id].recent_average_wage =
              store.recent_average_wage;
            store.weekly_stats.forEach((week, index) => {
              newCache[store.store_id].weeksByOffset[fetchOffset + index] = week;
            });
          });
          return newCache;
        });
      } catch (error) {
        if (cancelled) return;
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          setWeekCache({});
          setHasCompletedInitialLoad(false);
        }
        setOverviewError(status === 401 || status === 403
          ? "You don’t have access to this overview. Check your location access and try again."
          : "We couldn’t load the overview. Please try again.");
      } finally {
        if (cancelled) return;
        if (didFetchSuccessfully) {
          setHasCompletedInitialLoad(true);
        }
        setIsLoading(false);
      }
    };

    fetchMissingWeeks();
    return () => { cancelled = true; };
  }, [
    props.homeCompany,
    props.homeStore,
    weekOffset,
    weekCache,
    hasCompletedInitialLoad,
    overviewFetchRetryKey,
    overviewError,
  ]);

  const refreshOverview = () => {
    setOverviewError(null);
    setEmptyReason(null);
    setWeekCache({});
    setOverviewData([]);
    setHasCompletedInitialLoad(false);
    setIsLoading(true);
    setOverviewFetchRetryKey(prev => prev + 1);
  };

  const handlePreviousWeeks = () => {
    setWeekOffset((prev) => prev - 1);
  };

  const handleNextWeeks = () => {
    setWeekOffset((prev) => prev + 1);
  };

  const handleTabButtonPress = (tabName) => {
    setActiveTab(tabName);
  };

  const shouldShowInitialOverviewLoading =
    activeTab === "4-Week" &&
    overviewData.length === 0 &&
    (
      isLoading ||
      !hasCompletedInitialLoad ||
      Object.keys(weekCache).length > 0
    );

  return (
    <div className="bg-white min-h-screen">
      <div>
        <TopBarDisplayTitleNoLocationSelect title="Overview" />
      </div>
      <div className="mt-6 pb-8 px-6">
        {/* <div className="w-[400px] mb-6">
          <TabsTwo
            activeTab={activeTab}
            handleTabButtonPress={handleTabButtonPress}
            tab1="4-Week"
            tab2="Financial Forecasting"
          />
        </div> */}

        {/* Render both components but hide the inactive one */}
        <div style={{ display: activeTab === "4-Week" ? "block" : "none" }}>
          {!props.homeCompany || !props.homeStore ? (
            <p className="py-16 text-center text-slate-500">Select a company and location to view the overview.</p>
          ) : overviewError ? (
            <div role="alert" className="py-16 text-center">
              <p className="text-slate-600">{overviewError}</p>
              <button className="mt-4 rounded-lg bg-violet-600 px-4 py-2 text-white" onClick={refreshOverview}>Retry</button>
            </div>
          ) : shouldShowInitialOverviewLoading ? (
            <OverviewLoadingState />
          ) : emptyReason === "no_active_locations" ? (
            <div className="py-16 text-center">
              <h2 className="text-xl font-semibold text-slate-700">No active locations yet</h2>
              <p className="mt-2 text-slate-500">No active locations are available to you in this company.</p>
              <div className="mt-4 flex justify-center gap-3">
                {props.canOpenSetup && (
                  <button className="rounded-lg bg-violet-600 px-4 py-2 text-white" onClick={() => props.setCurrentTab("onboardingCenter")}>Open setup</button>
                )}
                <button className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700" onClick={refreshOverview}>Refresh overview</button>
              </div>
            </div>
          ) : (
            <FourWeekOverview
              {...props}
              overviewData={overviewData}
              weekOffset={weekOffset}
              handlePreviousWeeks={handlePreviousWeeks}
              handleNextWeeks={handleNextWeeks}
              isLoading={isLoading}
            />
          )}
        </div>
        <div style={{ display: activeTab === "Financial Forecasting" ? "block" : "none" }}>
          <FinancialForecastingOverview
            {...props}
            overviewData={overviewData}
            weekOffset={weekOffset}
          />
        </div>
      </div>
    </div>
  );
}

// A company switch must discard in-flight responses and local data before
// rendering the next tenant. Successful empty results revalidate on remount.
export default function OverviewDisplay(props) {
  return <OverviewContent key={props.homeCompany || "none"} {...props} />;
}
